import React from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  TextField,
  Autocomplete,
  useTheme,
  useMediaQuery,
  InputAdornment,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import { useNavigate } from "react-router-dom";
import { createFilterOptions } from "@mui/material/Autocomplete";
import themestyle from "../theme";
import { textFieldSX, FormLabel, autocompleteListboxSX } from "../utils/formField";
import { StudentAttendance } from "../interfaces/attendance.interface";
import { AddAttendanceModelProps } from "../interfaces/attendanceProps"

const ATTENDANCE_CATEGORIES = [
  "Drop-In (Walk-in)",
  "Trail Session",
  "Manual Override",
];

const filter = createFilterOptions<StudentAttendance>();
const ADD_NEW_OPTION_ID = "ADD_NEW_STUDENT_ACTION";

const AddAttendanceModel: React.FC<AddAttendanceModelProps> = ({
  open,
  onClose,
  students,
  onEnroll,
  loading = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const [selectedStudent, setSelectedStudent] = React.useState<StudentAttendance | null>(null);
  const [inputValue, setInputValue] = React.useState("");
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const [category, setCategory] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setSelectedStudent(null);
      setInputValue("");
      setIsDropdownOpen(false);
      setCategory(null);
    }
  }, [open]);

  const handleEnroll = () => {
    if (onEnroll && selectedStudent && category) {
      onEnroll({ studentId: selectedStudent.studentId, category });
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "5px",
          p: isMobile ? 1 : 2,
          margin: isMobile ? 2 : 4,
        },
      }}
    >
      <IconButton
        onClick={onClose}
        sx={{
          position: "absolute",
          right: isMobile ? 8 : 16,
          top: isMobile ? 8 : 16,
          color: "#9CA3AF",
          backgroundColor: "#F3F4F6",
          padding: isMobile ? "4px" : "8px",
          "&:hover": {
            backgroundColor: "#E5E7EB",
          },
        }}
        size="small"
      >
        <CloseIcon sx={{ fontSize: isMobile ? 18 : 22 }} />
      </IconButton>
      <DialogContent sx={{ py: isMobile ? 3 : 4, px: isMobile ? 2 : 3 }}>
        <Typography variant="h5" fontWeight={700} sx={{ color: "#111827", mb: 1, fontSize: isMobile ? "20px" : "24px" }}>
          Add Attendance
        </Typography>
        <Typography
          sx={{
            color: "#4B5563",
            lineHeight: 1.5,
            mb: 4,
            fontSize: isMobile ? "13px" : "16px",
          }}
        >
          Enroll a student who isn’t on the schedule for this session.
        </Typography>

        <Box mb={3}>
          <FormLabel>Student</FormLabel>
          <Autocomplete
            options={students}
            popupIcon={null} 
            getOptionLabel={(option: StudentAttendance) => 
               `${option.studentId || "-"} - ${option.studentName || ""}`
            }
            value={selectedStudent}
            onChange={(_, newValue) => {
              setSelectedStudent(newValue);
            }}
            sx={{
              ...textFieldSX,
              position: "relative",
              "& .MuiAutocomplete-endAdornment": {
                display: "none",
              },
            }}
            ListboxProps={{ 
              sx: {
                ...autocompleteListboxSX,
                maxHeight: "150px",
                "& .MuiAutocomplete-option": {
                  fontSize: "14px",
                  py: "8px",
                }
              } 
            }}
            renderInput={(params) => (
              <Box sx={{ position: "relative" }}>
                <TextField
                  {...params}
                  placeholder="Select Student"
                  InputProps={{
                    ...params.InputProps,
                  }}
                />
                <IconButton 
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate("/students/add");
                  }}
                  sx={{ 
                    position: "absolute",
                    right: 12,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: themestyle.colors.activeGreen,
                    p: 0,
                    zIndex: 1,
                  }}
                >
                  <PersonAddAlt1Icon sx={{ fontSize: 20 }} />
                </IconButton>
              </Box>
            )}
          />
        </Box>

        <Box mb={4}>
          <FormLabel>Attendance Category</FormLabel>
          <Autocomplete
            options={ATTENDANCE_CATEGORIES}
            value={category}
            onChange={(_, newValue) => setCategory(newValue)}
            sx={textFieldSX}
            ListboxProps={{ sx: autocompleteListboxSX }}
            renderInput={(params) => (
              <TextField 
                {...params} 
                placeholder="Select Category"
              />
            )}
          />
        </Box>

        <Box sx={{ display: "flex", gap: isMobile ? 1 : 2 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={onClose}
            sx={{
              flex: isMobile ? 0.35 : 0.8,
              py: isMobile ? 0.5 : 1.5,
              borderRadius: "5px",
              textTransform: "none",
              fontWeight: 700,
              color: "#111827",
              borderColor: "#E5E7EB",
              fontSize: isMobile ? "12px" : "16px",
              whiteSpace: "nowrap",
              minWidth: 0,
              "&:hover": {
                borderColor: "#D1D5DB",
                backgroundColor: "#F9FAFB",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={handleEnroll}
            disabled={!selectedStudent || !category || loading}
            sx={{
              flex: isMobile ? 1.65 : 1.2,
              py: isMobile ? 0.5 : 1.5,
              borderRadius: "5px",
              textTransform: "none",
              fontWeight: 700,
              backgroundColor: themestyle.colors.activeGreen,
              color: "white",
              fontSize: isMobile ? "12px" : "16px",
              whiteSpace: "nowrap",
              boxShadow: "none",
              "&:hover": {
                backgroundColor:themestyle.colors.activeGreen,
                boxShadow: "none",
              },
            }}
          >
            {loading ? "Saving..." : "Enroll & Mark Present"}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AddAttendanceModel;
